import React from 'react';
import { RQ_TENANT_GROUP_OVERRIDES_INHERITED } from 'Utils';
import { Button, Paper } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { UseSplitViewForm } from 'Components/Table/hooks/useSplitViewForm';
import { shouldShowCreateRule } from 'Selectors';
import { RoleService } from 'Services';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import { RulesTypes } from 'Common/enums';
import styles from './TenantGroupOverridePaper.css';
import DisplayContent from './DisplayContent';
import EditOverrideBtn from './EditOverrideBtn';
import AddOverrideBtn from './AddOverrideBtn';
import RemoveOverrideBtn from './RemoveOverrideBtn';

interface TenantGroupRuleSplitViewFormState {
    active: boolean;
}

interface Props extends Omit<UseSplitViewForm<TenantGroupRuleSplitViewFormState>, 'setRowId'> {
    paperClassName?: string;
    dataTestId: string;
    rule: Rule;
    readOnly: boolean;
    selectedRowId: string;
    tenantGroupId: string;
    mode?: string;
}

const TenantGroupOverridePaper = ({
    dataTestId,
    paperClassName,
    rule,
    readOnly,
    selectedRowId,
    tenantGroupId,
    mode
}: Props) => {
    const { t } = useTranslation();
    const isFetchingCount = useIsFetching([RQ_TENANT_GROUP_OVERRIDES_INHERITED]);
    const isMutatingCount = useIsMutating();
    const history = useHistory();
    const isLoading = Boolean(isFetchingCount) || Boolean(isMutatingCount);
    const hasOverride = rule?.overrideGroup === t('Overrides');
    // The state of "createRule" feature flag will control user's edit access
    // forbid adhoc edit when the current rule is adhoc and createRule feature flag is off
    const forbidAdhocOverride = !!rule?.nonTemplated && !shouldShowCreateRule();
    const showRuleTest = mode === 'sandbox';
    const ruleType: RulesTypes = rule.expression ? RulesTypes.ADVANCED : RulesTypes.SIMPLE;

    const handleClick = () => {
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Tenant Group Override - View Rule Test ',
                text: 'View Tenant Group Override Rule Test'
            }
        });
        history.push(
            // eslint-disable-next-line max-len
            `/global-rules/rule-tester/view/${rule?.id}?selectedRowId=${selectedRowId}&tenantGroupId=${tenantGroupId}`
        );
    };

    return (
        <Paper className={paperClassName}>
            <div className={styles.paperTitle}>
                {t('Tenant Group Override')}
                <div className={styles.buttonContainer}>
                    {hasOverride &&
                    RoleService.hasViewRuleTestRole &&
                    !RoleService.hasEditRuleOrBulkUploadAccess &&
                    showRuleTest ? (
                        <Button
                            className={styles.viewRuleTest}
                            dataTestId='view-rule-test'
                            variant='tertiary'
                            onClick={handleClick}
                            onKeyDown={handleClick}
                        >
                            {t('View Rule Test')}
                        </Button>
                    ) : null}
                    {forbidAdhocOverride ? null : (
                        <>
                            {hasOverride && (
                                <>
                                    <RemoveOverrideBtn id={rule?.id} showButton={!readOnly} ruleType={ruleType} />
                                    <EditOverrideBtn
                                        id={rule?.id}
                                        showButton={!readOnly}
                                        selectedRowId={selectedRowId}
                                    />
                                </>
                            )}
                            {!hasOverride && (
                                <AddOverrideBtn id={rule?.id} showButton={!readOnly} selectedRowId={selectedRowId} />
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className={styles.tenantGroupOverridePaper} data-testid={`${dataTestId}-override`}>
                {hasOverride && (
                    <DisplayContent
                        tenantGroupSpecificSetting={rule?._formatted?.inputDescription}
                        overrideStatusBoolean={rule.parameters.active}
                        isLoading={isLoading}
                        overrideStatus={rule._formatted?.active as string}
                    />
                )}
            </div>
        </Paper>
    );
};

export default TenantGroupOverridePaper;
